
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { useUpdateEventGuest, EventGuest } from "@/hooks/useEventGuests"
import { useToast } from "@/hooks/use-toast"

interface EditEventGuestModalProps {
  guest: EventGuest
  isOpen: boolean
  onClose: () => void
}

export const EditEventGuestModal = ({ guest, isOpen, onClose }: EditEventGuestModalProps) => {
  const [formData, setFormData] = useState({
    full_name: guest.full_name,
    email: guest.email || "",
    phone: guest.phone || "",
    handle: guest.handle || "",
    guest_type: guest.guest_type,
    rsvp_status: guest.rsvp_status,
    notes: guest.notes || "",
  })

  const updateGuest = useUpdateEventGuest()
  const { toast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.full_name.trim()) {
      toast({
        title: "Validation Error",
        description: "Guest name is required",
        variant: "destructive",
      })
      return
    }

    try {
      await updateGuest.mutateAsync({
        id: guest.id,
        full_name: formData.full_name.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        handle: formData.handle.trim() || null,
        guest_type: formData.guest_type,
        rsvp_status: formData.rsvp_status,
        notes: formData.notes.trim() || null,
      })
      onClose()
    } catch (error) {
      console.error("Error updating guest:", error)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Guest</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="full_name">Guest Name *</Label>
            <Input
              id="full_name"
              value={formData.full_name}
              onChange={(e) => handleInputChange("full_name", e.target.value)}
              placeholder="Enter guest name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="handle">Handle/Username</Label>
            <Input
              id="handle"
              value={formData.handle}
              onChange={(e) => handleInputChange("handle", e.target.value)}
              placeholder="@username (optional)"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="guest@example.com (optional)"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              placeholder="+1 234 567 8900 (optional)"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="guest_type">Guest Category</Label>
            <Select value={formData.guest_type} onValueChange={(value) => handleInputChange("guest_type", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Influencer">Influencer</SelectItem>
                <SelectItem value="Media">Media</SelectItem>
                <SelectItem value="VIP">VIP</SelectItem>
                <SelectItem value="Others">Others</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="rsvp_status">RSVP Status</Label>
            <Select value={formData.rsvp_status} onValueChange={(value) => handleInputChange("rsvp_status", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select RSVP status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Confirmed">Confirmed</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Invited">Invited</SelectItem>
                <SelectItem value="Declined">Declined</SelectItem>
                <SelectItem value="Suggested">Suggested</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              placeholder="Additional notes about the guest (optional)"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateGuest.isPending}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {updateGuest.isPending ? "Updating..." : "Update Guest"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
